import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import type { Venta, Configuracion } from '@/types/database'

const fmt = (n: number) =>
  new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  debito: 'Débito',
  credito: 'Crédito',
  cuenta_corriente: 'Cuenta corriente',
}

type TicketConfig = Pick<
  Configuracion,
  'nombre_negocio' | 'telefono' | 'direccion' | 'info_adicional' | 'mensaje_ticket' | 'formato_ticket'
>

// ─── Formato A4 ───────────────────────────────────────────────────────────────

const A4 = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica', fontSize: 9,
    paddingTop: 48, paddingBottom: 48, paddingLeft: 90, paddingRight: 90,
    color: '#1a1a0e', backgroundColor: '#ffffff',
  },
  header:    { alignItems: 'center', marginBottom: 6 },
  negocio:   { fontFamily: 'Helvetica-Bold', fontSize: 15, textAlign: 'center', marginBottom: 4, letterSpacing: -0.3 },
  headerSub: { fontSize: 8, color: '#6b6b5a', textAlign: 'center', marginBottom: 2 },
  sep:       { borderBottomWidth: 0.5, borderBottomColor: '#d0cdc8', marginTop: 10, marginBottom: 10 },
  sepBold:   { borderBottomWidth: 1, borderBottomColor: '#1a1a0e', marginTop: 10, marginBottom: 10 },
  eyebrow:   { fontSize: 7, textTransform: 'uppercase', letterSpacing: 1.2, color: '#9b9b8a', marginBottom: 5 },
  row:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 },
  label:     { fontSize: 9, color: '#3a3a2e' },
  value:     { fontFamily: 'Helvetica-Bold', fontSize: 9 },
  itemsHeaderRow: { flexDirection: 'row', paddingBottom: 4, borderBottomWidth: 0.5, borderBottomColor: '#d0cdc8', marginBottom: 5 },
  itemRow:   { flexDirection: 'row', marginBottom: 4 },
  colCant:   { width: 22, fontSize: 9, color: '#6b6b5a' },
  colNombre: { flex: 1, fontSize: 9, paddingRight: 4 },
  colPrecio: { width: 56, textAlign: 'right', fontSize: 8, color: '#6b6b5a' },
  colSub:    { width: 56, textAlign: 'right', fontFamily: 'Helvetica-Bold', fontSize: 9 },
  totalRow:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: 8, marginTop: 4, borderTopWidth: 1.5, borderTopColor: '#1a1a0e' },
  totalLabel:{ fontFamily: 'Helvetica-Bold', fontSize: 12 },
  totalValue:{ fontFamily: 'Helvetica-Bold', fontSize: 16 },
  footer:    { textAlign: 'center', color: '#9b9b8a', fontSize: 8, marginTop: 4 },
  pill:      { backgroundColor: '#fde8e0', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, fontSize: 7, color: '#e84e1b', fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', letterSpacing: 0.8 },
})

function TicketA4({ venta, config }: { venta: Venta; config: TicketConfig }) {
  const pago = venta.pagos?.[0]
  const clienteNombre = (venta.clientes as { nombre: string } | undefined)?.nombre
  const itemCount = venta.venta_items?.reduce((s, i) => s + i.cantidad, 0) ?? 0
  const fecha = new Date(venta.created_at)
  const fechaStr = fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const horaStr  = fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })

  return (
    <Page size="A4" style={A4.page}>
      <View style={A4.header}>
        <Text style={A4.negocio}>{config.nombre_negocio}</Text>
        {config.direccion && <Text style={A4.headerSub}>{config.direccion}</Text>}
        {config.telefono  && <Text style={A4.headerSub}>Tel: {config.telefono}</Text>}
      </View>
      <View style={A4.sep} />

      <View style={A4.row}>
        <View>
          <Text style={A4.eyebrow}>Comprobante de venta</Text>
          <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 13 }}>N° {venta.numero}</Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={{ fontSize: 9, color: '#3a3a2e' }}>{fechaStr}</Text>
          <Text style={{ fontSize: 8, color: '#9b9b8a' }}>{horaStr}</Text>
        </View>
      </View>

      {(clienteNombre || venta.tipo === 'mayorista') && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 5 }}>
          {clienteNombre && (
            <Text style={{ fontSize: 9, color: '#3a3a2e' }}>
              Cliente: <Text style={{ fontFamily: 'Helvetica-Bold' }}>{clienteNombre}</Text>
            </Text>
          )}
          {venta.tipo === 'mayorista' && <View style={A4.pill}><Text>Mayorista</Text></View>}
        </View>
      )}
      <View style={A4.sep} />

      <Text style={A4.eyebrow}>Productos · {itemCount} unidades</Text>
      <View style={A4.itemsHeaderRow}>
        <Text style={[A4.colCant,   { fontFamily: 'Helvetica-Bold', color: '#1a1a0e' }]}>Cant</Text>
        <Text style={[A4.colNombre, { fontFamily: 'Helvetica-Bold' }]}>Producto</Text>
        <Text style={[A4.colPrecio, { fontFamily: 'Helvetica-Bold', color: '#1a1a0e' }]}>P. Unit</Text>
        <Text style={[A4.colSub,    { fontFamily: 'Helvetica-Bold' }]}>Subtotal</Text>
      </View>
      {(venta.venta_items ?? []).map(item => {
        const nombre = (item.productos as { nombre: string } | undefined)?.nombre ?? '—'
        const marca  = (item.productos as { marca?: string } | undefined)?.marca
        const desc   = item.descuento > 0 ? ` (−${item.descuento}%)` : ''
        return (
          <View key={item.id} style={A4.itemRow}>
            <Text style={A4.colCant}>{item.cantidad}</Text>
            <View style={A4.colNombre}>
              <Text>{nombre}{desc}</Text>
              {marca && <Text style={{ fontSize: 7.5, color: '#9b9b8a' }}>{marca}</Text>}
            </View>
            <Text style={A4.colPrecio}>${fmt(item.precio_unitario)}</Text>
            <Text style={A4.colSub}>${fmt(item.subtotal)}</Text>
          </View>
        )
      })}

      {venta.descuento_total > 0 && (
        <>
          <View style={A4.sep} />
          <View style={A4.row}>
            <Text style={A4.label}>Subtotal sin descuento</Text>
            <Text style={A4.value}>${fmt(venta.subtotal)}</Text>
          </View>
          <View style={A4.row}>
            <Text style={{ fontSize: 9, color: '#22c55e' }}>Descuento</Text>
            <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9, color: '#22c55e' }}>−${fmt(venta.descuento_total)}</Text>
          </View>
        </>
      )}

      <View style={A4.totalRow}>
        <Text style={A4.totalLabel}>TOTAL</Text>
        <Text style={A4.totalValue}>${fmt(venta.total)}</Text>
      </View>
      <View style={A4.sep} />

      {pago && (
        <View>
          <Text style={A4.eyebrow}>Pago</Text>
          <View style={A4.row}>
            <Text style={A4.label}>{METODO_LABEL[pago.metodo] ?? pago.metodo}</Text>
            <Text style={A4.value}>${fmt(pago.monto)}</Text>
          </View>
          {pago.metodo === 'efectivo' && pago.monto > venta.total && (
            <View style={A4.row}>
              <Text style={A4.label}>Vuelto</Text>
              <Text style={A4.value}>${fmt(pago.monto - venta.total)}</Text>
            </View>
          )}
        </View>
      )}

      {venta.notas && (
        <>
          <View style={A4.sep} />
          <Text style={{ fontSize: 8, color: '#6b6b5a' }}>Notas: {venta.notas}</Text>
        </>
      )}
      {config.info_adicional && (
        <>
          <View style={A4.sep} />
          <Text style={{ fontSize: 8, color: '#6b6b5a', textAlign: 'center', lineHeight: 1.5 }}>
            {config.info_adicional}
          </Text>
        </>
      )}
      <View style={A4.sep} />
      <Text style={A4.footer}>{config.mensaje_ticket || 'Gracias por su compra'}</Text>
    </Page>
  )
}

// ─── Formato Térmica 80mm ─────────────────────────────────────────────────────
// Ancho: 80mm = 226.77pts. Margen: 4mm = 11.34pts c/lado. Usable: 72mm ≈ 204pts

const mm = (n: number) => n * (72 / 25.4)

const T = StyleSheet.create({
  page: {
    fontFamily: 'Courier',
    fontSize: 8,
    paddingTop: mm(5),
    paddingBottom: mm(8),
    paddingLeft: mm(4),
    paddingRight: mm(4),
    color: '#000000',
    backgroundColor: '#ffffff',
  },
  center:    { textAlign: 'center' },
  bold:      { fontFamily: 'Courier-Bold' },
  negocio:   { fontFamily: 'Courier-Bold', fontSize: 11, textAlign: 'center', marginBottom: 2 },
  sub:       { fontSize: 7, textAlign: 'center', marginBottom: 1 },
  sep:       { borderBottomWidth: 0.5, borderBottomColor: '#000', marginTop: 5, marginBottom: 5 },
  row:       { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
  eyebrow:   { fontSize: 6.5, textTransform: 'uppercase', letterSpacing: 0.8, color: '#555', marginBottom: 3 },
  itemRow:   { flexDirection: 'row', marginBottom: 3 },
  iCant:     { width: mm(8), fontSize: 8, color: '#333' },
  iNombre:   { flex: 1, fontSize: 8, paddingRight: 3 },
  iSub:      { width: mm(18), textAlign: 'right', fontFamily: 'Courier-Bold', fontSize: 8 },
  iUnit:     { fontSize: 6.5, color: '#666' },
  totalRow:  { flexDirection: 'row', justifyContent: 'space-between', marginTop: 3, paddingTop: 4, borderTopWidth: 1, borderTopColor: '#000' },
  totalL:    { fontFamily: 'Courier-Bold', fontSize: 11 },
  totalV:    { fontFamily: 'Courier-Bold', fontSize: 13 },
  footer:    { textAlign: 'center', fontSize: 7.5, marginTop: 3, color: '#333' },
})

function TicketTermico({ venta, config }: { venta: Venta; config: TicketConfig }) {
  const pago = venta.pagos?.[0]
  const clienteNombre = (venta.clientes as { nombre: string } | undefined)?.nombre
  const fecha = new Date(venta.created_at)
  const fechaStr = fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const horaStr  = fecha.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })

  return (
    <Page size={[mm(80), mm(297)]} style={T.page}>

      {/* Cabecera */}
      <Text style={T.negocio}>{config.nombre_negocio}</Text>
      {config.direccion && <Text style={T.sub}>{config.direccion}</Text>}
      {config.telefono  && <Text style={T.sub}>Tel: {config.telefono}</Text>}

      <View style={T.sep} />

      {/* Número y fecha */}
      <View style={T.row}>
        <Text style={[T.bold, { fontSize: 9 }]}>N° {venta.numero}</Text>
        <Text style={{ fontSize: 7.5 }}>{fechaStr} {horaStr}</Text>
      </View>
      {venta.tipo === 'mayorista' && (
        <Text style={{ fontSize: 7, fontFamily: 'Courier-Bold', marginBottom: 2 }}>*** MAYORISTA ***</Text>
      )}
      {clienteNombre && (
        <Text style={{ fontSize: 7.5, marginBottom: 2 }}>Cliente: {clienteNombre}</Text>
      )}

      <View style={T.sep} />

      {/* Items */}
      <Text style={T.eyebrow}>Detalle</Text>
      {(venta.venta_items ?? []).map(item => {
        const nombre = (item.productos as { nombre: string } | undefined)?.nombre ?? '—'
        const desc   = item.descuento > 0 ? ` -${item.descuento}%` : ''
        return (
          <View key={item.id} style={T.itemRow}>
            <Text style={T.iCant}>{item.cantidad}x</Text>
            <View style={T.iNombre}>
              <Text>{nombre}{desc}</Text>
              <Text style={T.iUnit}>${fmt(item.precio_unitario)} c/u</Text>
            </View>
            <Text style={T.iSub}>${fmt(item.subtotal)}</Text>
          </View>
        )
      })}

      {venta.descuento_total > 0 && (
        <View style={T.row}>
          <Text style={{ fontSize: 7.5 }}>Descuento</Text>
          <Text style={{ fontSize: 7.5, fontFamily: 'Courier-Bold' }}>-${fmt(venta.descuento_total)}</Text>
        </View>
      )}

      {/* Total */}
      <View style={T.totalRow}>
        <Text style={T.totalL}>TOTAL</Text>
        <Text style={T.totalV}>${fmt(venta.total)}</Text>
      </View>

      <View style={T.sep} />

      {/* Pago */}
      {pago && (
        <>
          <View style={T.row}>
            <Text style={{ fontSize: 8 }}>{METODO_LABEL[pago.metodo] ?? pago.metodo}</Text>
            <Text style={[T.bold, { fontSize: 8 }]}>${fmt(pago.monto)}</Text>
          </View>
          {pago.metodo === 'efectivo' && pago.monto > venta.total && (
            <View style={T.row}>
              <Text style={{ fontSize: 8 }}>Vuelto</Text>
              <Text style={[T.bold, { fontSize: 8 }]}>${fmt(pago.monto - venta.total)}</Text>
            </View>
          )}
        </>
      )}

      {/* Info adicional + mensaje */}
      {config.info_adicional && (
        <>
          <View style={T.sep} />
          <Text style={{ fontSize: 7, textAlign: 'center', lineHeight: 1.4 }}>{config.info_adicional}</Text>
        </>
      )}

      <View style={T.sep} />
      <Text style={T.footer}>{config.mensaje_ticket || 'Gracias por su compra'}</Text>
    </Page>
  )
}

// ─── Exportación principal ────────────────────────────────────────────────────

export function TicketDocument({ venta, config }: { venta: Venta; config: TicketConfig }) {
  return (
    <Document title={`Ticket #${venta.numero}`}>
      {config.formato_ticket === 'a4'
        ? <TicketA4     venta={venta} config={config} />
        : <TicketTermico venta={venta} config={config} />
      }
    </Document>
  )
}
